type Args = Record<string, unknown>

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function ellipsize(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function parseDate(value: unknown): Date | null {
  const text = asString(value)
  if (!text) return null
  const date = new Date(text)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatDate(date: Date): string {
  return `${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

function formatDateRange(rawFrom: unknown, rawTo: unknown): string | null {
  const from = parseDate(rawFrom)
  const to = parseDate(rawTo)
  if (!from && !to) return null
  if (from && to) {
    const sameMonth =
      from.getUTCFullYear() === to.getUTCFullYear() &&
      from.getUTCMonth() === to.getUTCMonth()
    if (sameMonth) return formatDate(from)
    return `${formatDate(from)} → ${formatDate(to)}`
  }
  if (from) return `from ${formatDate(from)}`
  if (to) return `through ${formatDate(to)}`
  return null
}

function summarizeFilters(filters: Args | null): string[] {
  if (!filters) return []
  const parts: string[] = []
  const range = formatDateRange(filters.date_from, filters.date_to)
  if (range) parts.push(range)

  const kinds = asArray(filters.kinds)
  if (kinds && kinds.length > 0) {
    parts.push(kinds.join(', '))
  }

  const categories = asArray(filters.categories)
  if (categories && categories.length > 0) {
    parts.push(
      categories.length <= 2
        ? categories.join(', ')
        : `${categories.length} categories`,
    )
  }

  const account = asString(filters.account_number)
  if (account) parts.push(`account ${account}`)

  return parts
}

function summarizeQueryTransactions(args: Args): string | null {
  const filters = isObject(args.filters) ? args.filters : null
  const parts = summarizeFilters(filters)
  const limit = asNumber(args.limit)
  if (limit) parts.push(`limit ${limit}`)
  return parts.length > 0 ? parts.join(', ') : null
}

function summarizeAggregate(args: Args): string | null {
  const groupBy = asString(args.group_by)
  const metric = asString(args.metric)
  const parts: string[] = []
  if (metric && groupBy) parts.push(`${metric} by ${groupBy}`)
  else if (groupBy) parts.push(`by ${groupBy}`)
  else if (metric) parts.push(metric)

  const filters = isObject(args.filters) ? args.filters : null
  parts.push(...summarizeFilters(filters))
  return parts.length > 0 ? parts.join(', ') : null
}

function summarizeCompare(args: Args): string | null {
  const a = isObject(args.period_a) ? args.period_a : null
  const b = isObject(args.period_b) ? args.period_b : null
  const aRange = a ? formatDateRange(a.date_from, a.date_to) : null
  const bRange = b ? formatDateRange(b.date_from, b.date_to) : null
  if (aRange && bRange) return `${aRange} vs ${bRange}`
  return aRange ?? bRange
}

function summarizeSearch(args: Args): string | null {
  const query = asString(args.query)
  if (!query) return null
  const filters = isObject(args.filters) ? args.filters : null
  const filterParts = summarizeFilters(filters)
  return [`"${ellipsize(query, 40)}"`, ...filterParts].join(', ')
}

function summarizeReadSkill(args: Args): string | null {
  const name = asString(args.name)
  return name ? `"${name}"` : null
}

function summarizeRunPython(args: Args): string | null {
  const code = asString(args.code)
  if (!code) return null
  const firstLine = code.split('\n').find((line) => line.trim()) ?? code
  return ellipsize(firstLine.trim(), 60)
}

function summarizeExportTransactions(args: Args): string | null {
  const title = asString(args.title)
  if (title) return title
  const filters = isObject(args.filters) ? args.filters : null
  const filterParts = summarizeFilters(filters)
  return filterParts.length > 0 ? filterParts.join(', ') : 'all transactions'
}

const ARGS_SUMMARIZERS: Record<string, (args: Args) => string | null> = {
  query_transactions: summarizeQueryTransactions,
  aggregate_transactions: summarizeAggregate,
  compare_periods: summarizeCompare,
  search_transactions_semantic: summarizeSearch,
  read_skill: summarizeReadSkill,
  run_python: summarizeRunPython,
  export_transactions: summarizeExportTransactions,
}

export function summarizeArgs(
  toolName: string,
  rawInput: unknown,
): string | null {
  const summarizer = ARGS_SUMMARIZERS[toolName]
  if (!summarizer || !isObject(rawInput)) return null
  return summarizer(rawInput)
}

function pluralize(count: number, singular: string, plural?: string): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

function summarizeQueryResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const txns = asArray(payload.transactions) ?? []
  const total = asNumber(payload.total_count) ?? txns.length
  const cursor = payload.next_cursor
  const suffix = cursor ? ' (more available)' : ''
  return `${pluralize(total, 'transaction')}${suffix}`
}

function formatMetricTotal(metric: string | null, total: number): string {
  if (metric === 'count') return pluralize(total, 'transaction')
  if (
    metric === 'sum' ||
    metric === 'avg' ||
    metric === 'min' ||
    metric === 'max'
  ) {
    return currencyFormatter.format(total)
  }
  return total.toLocaleString()
}

function summarizeAggregateResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const bucketCount =
    asNumber(payload.bucket_count) ?? (asArray(payload.buckets) ?? []).length
  const total = asNumber(payload.total)
  const metric = asString(payload.metric)
  const totalText =
    total !== null ? `, total ${formatMetricTotal(metric, total)}` : ''
  return `${pluralize(bucketCount, 'bucket')}${totalText}`
}

function summarizeCompareResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const delta = asNumber(payload.delta)
  const deltaPercent = asNumber(payload.delta_percent)
  if (delta === null && deltaPercent === null) return null
  const sign = delta !== null && delta >= 0 ? '+' : ''
  const parts: string[] = []
  if (delta !== null) parts.push(`Δ ${sign}${currencyFormatter.format(delta)}`)
  if (deltaPercent !== null) {
    const pctSign = deltaPercent >= 0 ? '+' : ''
    parts.push(`(${pctSign}${deltaPercent.toFixed(1)}%)`)
  }
  return parts.join(' ')
}

function summarizeSearchResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const results = asArray(payload.results)
  if (!results) return null
  return pluralize(results.length, 'result')
}

function summarizeInsightsResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const status = asString(payload.status)
  return status ?? null
}

function summarizeRunPythonResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const exitCode = asNumber(payload.exit_code)
  if (exitCode !== null && exitCode !== 0) {
    const stderr = asString(payload.stderr)
    const firstError =
      stderr?.split('\n').find((line) => line.trim()) ?? `exit ${exitCode}`
    return ellipsize(firstError.trim(), 60)
  }
  const renderedRows = isObject(payload.render)
    ? asArray(payload.render.rows)
    : null
  if (renderedRows) return pluralize(renderedRows.length, 'row')
  const stdout = asString(payload.stdout)
  if (!stdout) return 'no output'
  const nonBlankLines = stdout.split('\n').filter((line) => line.trim())
  const lastLine = nonBlankLines[nonBlankLines.length - 1] ?? stdout
  return ellipsize(lastLine.trim(), 60)
}

function summarizeExportTransactionsResult(payload: unknown): string | null {
  if (!isObject(payload)) return null
  const spec = isObject(payload.spec) ? payload.spec : null
  const rowCount = asNumber(spec?.row_count) ?? asNumber(payload.row_count)
  if (rowCount === null) return null
  const truncated = (spec?.truncated ?? payload.truncated) === true
  const total = asNumber(spec?.total_count) ?? asNumber(payload.total_count)
  const formatted = rowCount.toLocaleString()
  if (truncated && total && total > rowCount) {
    return `${formatted} of ${total.toLocaleString()} rows`
  }
  return `${formatted} rows`
}

const RESULT_SUMMARIZERS: Record<string, (payload: unknown) => string | null> =
  {
    query_transactions: summarizeQueryResult,
    aggregate_transactions: summarizeAggregateResult,
    compare_periods: summarizeCompareResult,
    search_transactions_semantic: summarizeSearchResult,
    get_existing_insights: summarizeInsightsResult,
    run_python: summarizeRunPythonResult,
    export_transactions: summarizeExportTransactionsResult,
  }

export function summarizeResult(
  toolName: string,
  payload: unknown,
): string | null {
  const summarizer = RESULT_SUMMARIZERS[toolName]
  if (!summarizer) return null
  return summarizer(payload)
}
